import React from "react";
import { useTranslation } from "react-i18next";
import useConfirm from "../../../../../../hooks/useConfirm";
import { useGetWarehouseCodePrefix } from "./useGetWarehouseCodePrefix";
import useUpdateWarehouseCodePrefix from "./useUpdateWarehouseCodePrefix";

export function useWarehouseCodePrefixField(clinicId) {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useGetWarehouseCodePrefix(clinicId);
  const { updateCodePrefix, isUpdating } = useUpdateWarehouseCodePrefix(clinicId);
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("UpdatePrefixForExistingItems"),
    t("Yes"),
    t("No"),
  );

  const [prefix, setPrefix] = React.useState("");
  const [initialized, setInitialized] = React.useState(false);
  const [conflicts, setConflicts] = React.useState([]);
  const originalPrefixRef = React.useRef("");

  React.useEffect(() => {
    if (!initialized && data) {
      const value = data?.prefix ?? "";
      setPrefix(value);
      originalPrefixRef.current = value;
      setInitialized(true);
    }
  }, [data, initialized]);

  async function save() {
    if (prefix === originalPrefixRef.current) {
      // Nothing changed -- skip the confirm dialog and network call so a
      // page-wide save doesn't nag about the prefix when only some other
      // setting was actually edited.
      return;
    }
    getMessage(t("UpdatePrefixForExistingItemsDescription"));
    const applyToExisting = await confirm();
    const result = await updateCodePrefix(prefix, applyToExisting);
    if (result) {
      originalPrefixRef.current = prefix;
      refetch();
      if (result.conflicts?.length) {
        setConflicts(result.conflicts);
      }
    }
  }

  return {
    prefix,
    setPrefix,
    save,
    isLoading,
    isUpdating,
    conflicts,
    clearConflicts: () => setConflicts([]),
    ConfirmationDialog,
  };
}
